import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ConfiguratorService } from './configurator-service';

/**
 * Registry for managing configurator services.
 *
 * This service tracks all registered ConfiguratorService instances
 * and provides a way to reset them all when the configurator instance changes.
 *
 * ## Usage
 *
 * In the configurator component or repository service:
 * ```typescript
 * // When destroying an instance
 * this.serviceRegistry.destroyAllServices();
 * ```
 *
 * Services register themselves automatically in their constructor:
 * ```typescript
 * @Injectable()
 * export class MyService extends ConfiguratorService {
 *     constructor(
 *         @Optional() @Inject(CONFIGURATOR_CONTEXT) context: ConfiguratorContext | null,
 *         private serviceRegistry: ConfiguratorServiceRegistry
 *     ) {
 *         super(context);
 *         serviceRegistry.register(this);
 *     }
 * }
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class ConfiguratorServiceRegistry implements OnDestroy {
    private services = new Set<ConfiguratorService>();
    private destroy$ = new Subject<void>();

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.services.clear();
    }

    /**
     * Register a service with the registry.
     * Automatically unregisters on service destroy.
     *
     * Only services with instance scope (created with CONFIGURATOR_CONTEXT)
     * are registered. Legacy services without context are skipped to prevent
     * them from receiving teardown calls that would crash their subscriptions.
     *
     * @param service The service to register
     */
    register(service: ConfiguratorService): void {
        // Skip legacy services that don't have instance context.
        // These services should not subscribe to instance-scoped NgRx state.
        if (!service.getInstanceId()) {
            console.warn(
                `[ConfiguratorServiceRegistry] Skipping registration of ${service.constructor.name} - no instance context`
            );
            return;
        }

        this.services.add(service);

        // Auto-unregister when service is destroyed
        service.onDestroy$.pipe(
            take(1),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.services.delete(service);
        });
    }

    /**
     * Unregister a service from the registry.
     * @param service The service to unregister
     */
    unregister(service: ConfiguratorService): void {
        this.services.delete(service);
    }

    /**
     * Get the number of registered services.
     */
    get serviceCount(): number {
        return this.services.size;
    }

    /**
     * Synchronously tear down all registered services.
     * Must be called BEFORE moduleRef.destroy() to prevent
     * NgRx state emissions reaching dead subscriptions.
     */
    destroyAllServices(): void {
        const snapshot = Array.from(this.services);
        snapshot.forEach(service => {
            try {
                service.teardown();
            } catch (error) {
                console.error('Error tearing down service:', error);
            }
        });
        this.services.clear();
    }

    /**
     * Synchronously tear down services belonging to a specific instance.
     * Only services with matching instanceId are torn down; others are left intact.
     */
    destroyServicesByInstance(instanceId: string): void {
        const snapshot = Array.from(this.services);
        snapshot.forEach(service => {
            if (service.getInstanceId() === instanceId) {
                try {
                    service.teardown();
                } catch (error) {
                    console.error('Error tearing down service:', error);
                }
                this.services.delete(service);
            }
        });
    }
}
